import { AddBox, Close } from '@mui/icons-material';
import { Box, Divider, Typography } from '@mui/material';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';

import { TranslateText } from '../../../../../../application/language/TranslateText';
import { Colors } from '../../../../../design/color/Colors';
import { useI18n } from '../../../../../../application/language/useI18n';
import { ICommonProperties } from '../../../../../../common/properties/ICommonProperties';
import { CodeEditor } from '../../../../../design/codeEditor/CodeEditor';
import { PracticePassage } from './parts/PracticePassage';
import { PracticeResult } from './parts/PracticeResult';
import { PracticeTask } from './PracticeTask';
import { IUnitPracticeDomain } from './store/IUnitPracticeDomain';

export interface IPracticeProperties extends ICommonProperties {
  domain: IUnitPracticeDomain;
}

export const Practice = observer(({ domain }: IPracticeProperties) => {
  const [isExpand, setIsExpand] = useState<boolean>(false);
  const { translate } = useI18n();

  useEffect(() => {
    domain.boot();
  }, [domain]);

  const showEditor = domain.ui.pickedLang.value !== null && domain.ui.contentPage.value === 'task';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flex: '0.7',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflowY: 'auto',
            background: Colors.bgElevated,
          }}
        >
          {domain.ui.contentPage.value === 'task' && domain.ui.pickedTask.value !== null && (
            <PracticeTask
              task={domain.ui.pickedTask.value}
              addLanguages={domain.addLanguages}
              pickedLang={domain.ui.pickedLang.value}
              pickLang={domain.pickLang}
            />
          )}
          {domain.ui.contentPage.value === 'time' && <PracticePassage unitDomain={domain.unitDomain} />}
          {domain.ui.contentPage.value === 'result' && <PracticeResult unitDomain={domain.unitDomain} />}
        </Box>

        <Divider orientation="vertical" />
        <Box
          sx={{
            padding: '8px 32px',
            flex: '0.3',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            background: Colors.bgElevated,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '9px',
              }}
            >
              <Typography variant="h6">
                <TranslateText i18nKey={'createUnit.task'} />
              </Typography>
              <AddBox sx={{ cursor: 'pointer' }} onClick={() => domain.addTask()} />
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '9px',

                marginBottom: '35px',
              }}
            >
              {domain.unitDomain.ui.unit.entity.practice.tasks.map((task, index) => (
                <Box
                  key={index}
                  onClick={() => {
                    domain.pickTask(task.pseudoId);
                  }}
                  sx={{
                    display: 'flex',
                    cursor: 'pointer',
                    justifyContent: 'space-between',
                    background: task.pseudoId === domain.ui.pickedTask.value?.pseudoId ? Colors.bgSurface : '',
                    padding: '8px 12px',
                    marginBottom: '8px',
                    borderRadius: '8px',
                    '&:hover': { background: Colors.bgSurface },
                    '&:hover .question__delete': { visibility: 'visible' },
                  }}
                >
                  <Typography variant="body1">{`${translate('phrases.task')} ${task.sortIndex}`}</Typography>
                  <Close
                    className="question__delete"
                    sx={{ visibility: 'hidden', cursor: 'pointer' }}
                    onClick={(event) => {
                      event.stopPropagation();
                      domain.onDelete(index);
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>

          <Typography variant="h6" sx={{ marginBottom: '9px' }}>
            {translate('phrases.settings')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '9px', marginBottom: '32px' }}>
            <Typography
              onClick={() => {
                domain.changeContent('time');
              }}
              variant="body1"
              sx={{
                paddingLeft: '12px',
                paddingTop: '4px',
                paddingBottom: '4px',
                cursor: 'pointer',
                '&:hover': { background: Colors.bgSurface },
                background: domain.ui.contentPage.value === 'time' ? Colors.bgSurface : 'inherit',
              }}
            >
              {translate('passingResults.completionTimeAndReward')}
            </Typography>
            <Typography
              onClick={() => {
                domain.changeContent('result');
              }}
              variant="body1"
              sx={{
                paddingLeft: '12px',
                paddingTop: '4px',
                paddingBottom: '4px',
                cursor: 'pointer',
                '&:hover': { background: Colors.bgSurface },
                background: domain.ui.contentPage.value === 'result' ? Colors.bgSurface : 'inherit',
              }}
            >
              {translate('phrases.results')}
            </Typography>
          </Box>
        </Box>
      </Box>
      {showEditor && (
        <Box
          sx={isExpand ? {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1300,
            bgcolor: 'background.default',
            overflow: 'hidden',
          } : {
            flex: 1,
            minHeight: '300px',
            overflow: 'hidden',
            background: Colors.bgElevated,
          }}
        >
          <CodeEditor lang={domain.ui.pickedLang.value} isExpand={isExpand} handleExpand={setIsExpand} />
        </Box>
      )}
    </Box>
  );
});
